'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { z } from 'zod';

import {
  DecarbonizationWorkspace,
  type DecarbonizationSection,
} from '@/features/ghg/components/decarbonization/DecarbonizationWorkspace';
import i18n from '@/lib/i18n';

const decarbonizationSections = [
  'overview',
  'targets',
  'actions-levers',
  'scenarios',
  'kpis',
  'roadmap',
  'sbti-alignment',
  'hotspots',
  'financial-impact',
  'ai-insights',
  'legacy',
] as const;

type DecarbonizationSearchSection = (typeof decarbonizationSections)[number];

const targetsSearchSchema = z.object({
  section: z.enum(decarbonizationSections).optional(),
});

export const Route = createFileRoute('/_authenticated/ghg/targets/')({
  validateSearch: targetsSearchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.decarbonization'),
  }),
  component: DecarbonizationPage,
});

function normalizeSection(section: DecarbonizationSearchSection | undefined): DecarbonizationSection | undefined {
  if (
    section === 'roadmap' ||
    section === 'sbti-alignment' ||
    section === 'ai-insights' ||
    section === 'legacy'
  ) {
    return 'overview';
  }
  return section;
}

function DecarbonizationPage() {
  const navigate = useNavigate();
  const { section } = Route.useSearch();

  const activeSection = useMemo<DecarbonizationSection>(
    () => normalizeSection(section) ?? 'overview',
    [section]
  );

  const handleSectionChange = (nextSection: DecarbonizationSection) => {
    void navigate({
      to: '/ghg/targets',
      search: { section: nextSection },
      replace: true,
    });
  };

  return (
    <DecarbonizationWorkspace
      activeSection={activeSection}
      onSectionChange={handleSectionChange}
    />
  );
}
